
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Find payment providers who have partnered with Plaid to provide general purpose ACH money movement'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Auth Payment Partners' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Many Plaid developers choose to use a Plaid partner to move money with Auth. You will use Plaid Link initialized with the Auth product, but instead of using Plaid Auth endpoints, you will use endpoints from the payment platform, giving you access to payment functionality while freeing you from having to securely store sensitive bank account information. The table below contains a list of partners and a set of customized instructions for integrating with each partner. `}</p>
    <h5 {...{
      "id": "global-payments-options"
    }}>{`Global payments options`}</h5>
    <p>{`Money movement via Plaid partners is only available for financial institutions within the US (with multiple providers below) and Canada (with `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships/vopay"
      }}>{`VoPay`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships/modern-treasury"
      }}>{`Modern Treasury`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships/finix"
      }}>{`Finix`}</a>{`). In Europe, `}<a parentName="p" {...{
        "href": "/docs/payment-initiation"
      }}>{`Payment Initiation`}</a>{` provides an easy and secure way to transfer funds.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Partner`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/dwolla"
            }}>{`Dwolla`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with Dwolla's ACH API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/square"
            }}>{`Square`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with the Square Web Payments SDK`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/achq"
            }}>{`ACHQ`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with ACHQ's ACH API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/adp-roll"
            }}>{`Roll by ADP`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Authenticate your customers' bank accounts for secure payroll direct deposit support.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/adyen"
            }}>{`Adyen`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Validate bank accounts and reduce returns through an end-to-end pay-with-your-bank solution with Adyen`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/alpaca"
            }}>{`Alpaca`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Use Alpaca with Plaid Auth to send and receive payments`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/ansa"
            }}>{`Ansa`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with the Ansa API to enable wallet funding over ACH`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/apex-clearing"
            }}>{`Apex Fintech Solutions`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your investors' bank accounts for use with Apex Fintech Solutions Cash API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/astra"
            }}>{`Astra`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customer's bank accounts for automated ACH transfers through the Astra platform`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/atomic"
            }}>{`Atomic`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts to seamlessly fund investment accounts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/bakkt"
            }}>{`Bakkt`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customer's accounts to use with Bakkt Fiat Services API for ACH based money movement`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/bond"
            }}>{`Bond`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with Bond's ACH Transfers API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/check"
            }}>{`Check`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Check lets you embed payroll in your product and easily configure and authenticate direct deposit payments`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/checkbook"
            }}>{`Checkbook`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with Checkbook's payment solution -- including ACH, real-time payments, push to card, virtual cards, and checks`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/checkout"
            }}>{`Checkout.com`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate bank account details for use with Checkout.com's Unified Payments API, and unlock unrivaled payment performance`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/drivewealth"
            }}>{`DriveWealth`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enable your customers to instantly and securely fund DriveWealth supported investment accounts by linking their bank account with Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/finix"
            }}>{`Finix`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly add customer bank accounts to Finix to accept and send payments with tokenized payment information. It's fast, frictionless, and secure.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/frame"
            }}>{`Frame`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly verify bank accounts to power payments, compliance, and billing in one unified platform`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/galileo"
            }}>{`Galileo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for account opening and funding`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/gusto"
            }}>{`Gusto`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Build payroll with Gusto, then use Plaid Auth to instantly connect your customers' bank accounts and run payroll faster`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/highnote"
            }}>{`Highnote`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customer's bank accounts for use with the Highnote Platform to store account details, transfer funds, and make payments`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/lithic"
            }}>{`Lithic`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instant bank authentication for ACH payments and card loads`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/marqeta"
            }}>{`Marqeta`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Integrate Plaid and Marqeta's APIs to seamlessly authenticate your customer's bank account prior to an ACH transfer`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/modern-treasury"
            }}>{`Modern Treasury`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with Modern Treasury's ACH API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/moov"
            }}>{`Moov`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts to enable them to accept, store, and disburse funds with Moov`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/open-ledger"
            }}>{`Open Ledger`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Connect your customer's bank account to Open Ledger's embedded accounting API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/riskified"
            }}>{`Riskified`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide fraud screening and ACH payment guarantee`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/sardine"
            }}>{`Sardine`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Protect your users by using Sardine's all-in-one fraud and compliance API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/scribeup"
            }}>{`ScribeUp`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Embed subscription management into your digital banking experience`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/seamless-ach"
            }}>{`Seamless ACH`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enhance your checkout and reduce returns with Seamless ACH. Utilize Plaid's verification and real-time balance checks to instantly debit and credit bank accounts.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/sila-money"
            }}>{`Sila Money`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Banking, Digital Wallets, and ACH Payments API for software teams`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/svb"
            }}>{`Silicon Valley Bank`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with Silicon Valley Bank's ACH API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/straddle"
            }}>{`Straddle`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Integrate Plaid with Straddle to deliver secure, identity-linked bank payments that settle quickly and feel as simple and reliable as using a debit card.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/stripe"
            }}>{`Stripe`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate your customers' bank accounts for use with Stripe's ACH API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/taba-pay"
            }}>{`TabaPay`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly process ACH/RTP/FedNow with TabaPay's Unified API using your Plaid processor token`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/treasury-prime"
            }}>{`Treasury Prime`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Send payments and create counterparties with the Treasury Prime API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/unit"
            }}>{`Unit`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly authenticate counterparties' bank accounts for use with the Unit API`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/vopay"
            }}>{`VoPay`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Eliminate inefficiencies of US and Canadian online bank account payments with VoPay's EFT / ACH and Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/webull"
            }}>{`Webull`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instantly connect and authenticate your customers' bank accounts to fund their Webull investment accounts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/wedbush"
            }}>{`Wedbush`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid-linked bank accounts can be connected to fund or withdraw from accounts on the Wedbush Securities platform.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/auth/partnerships/zero-hash"
            }}>{`Zero Hash`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Use Zero Hash with Plaid Auth to embed bank account linking for ACH-funded crypto trades`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`For more information, see the full list of partners in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/integrations"
      }}>{`Plaid Dashboard`}</a>{`.`}</p>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of an app that incorporates the creation of processor tokens, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that creates a processor token to send to your payment partner. The processor token creation code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L186-L195"
      }}>{`items.js`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
